// src/components/banks/BanksTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetBanks from "../../hooks/banks/useGetBanks";
import useDeleteBank from "../../hooks/banks/useDeleteBank";
import useDeleteAllBanks from "../../hooks/banks/useDeleteAllBanks";
import useRestoreBank from "../../hooks/banks/useRestoreBank";
import useRestoreAllBanks from "../../hooks/banks/useRestoreAllBanks";
import useForceDeleteBank from "../../hooks/banks/useForceDeleteBank";
import useForceDeleteAllBanks from "../../hooks/banks/useForceDeleteAllBanks";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import BanksTableRow from "./Table/BanksTableRow";
import BanksTableColumns from "./Table/BanksTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const BanksTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("banks_list"),
            create: has("banks_create"),
            edit: has("banks_edit"),
            delete: has("banks_delete"),
            forceDelete: has("banks_force_delete"),
            archiveList: has("banks_archive_list"),
            restore: has("banks_restore"),
            restoreAll: has("banks_restore-all-table"),
            deleteAll: has("banks_delete-all-table"),
            massForceDeleteAll: has("banks_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/banks/archive");

    // CRUD Hooks
    const { deleteBank, isDelete } = useDeleteBank();
    const { deleteAllBanks, isDeleteAll } = useDeleteAllBanks();
    const { restoreBank, isRestore } = useRestoreBank();
    const { restoreAllBanks, isRestoreAll } = useRestoreAllBanks();
    const { forceDeleteBank, isForceDelete } = useForceDeleteBank();
    const { forceDeleteAllBanks, isForceDeleteAll } = useForceDeleteAllBanks();

    // Custom Hook
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/banks",
        deleteSingle: deleteBank,
        deleteAll: deleteAllBanks,
        restoreSingle: restoreBank,
        restoreAll: restoreAllBanks,
        forceDeleteSingle: forceDeleteBank,
        forceDeleteAll: forceDeleteAllBanks,
        isArchive,
    });

    const [statusFilter, setStatusFilter] = React.useState("");

    const filters = useMemo(
        () => ({
            search: search || undefined,
            ...(statusFilter === "active" ? { is_active: true } : statusFilter === "disabled" ? { is_active: false } : {}),
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, sortBy, sortDir, isArchive]
    );

    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter]
    );

    const handleClearAllFilters = () => {
        setStatusFilter("");
    };

    const { isLoading, error, banks, meta } = useGetBanks(page, perPage, filters);

    const items = useMemo(() => banks?.items || [], [banks]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <BanksTableRow
            key={params.item.id}
            bank={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive ? !perms.massForceDeleteAll : !perms.deleteAll;

    const title = isArabic ? "بنوك الشركة" : "Company Banks";
    const subtitle = isArchive
        ? isArabic
            ? "عرض البنوك المحذوفة (الأرشيف)"
            : "Archived banks (soft deleted)"
        : isArabic
            ? "إدارة حسابات الشركة البنكية"
            : "Manage company bank accounts";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة بنك جديد"
            : "Create new bank";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للاستعادة" : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllBanks(selectedIds);
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للإنشاء" : "No create permission"
                );
                return;
            }

            navigate("/banks/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/banks/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/banks"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={BanksTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={isArchive ? "لا توجد بنوك في الأرشيف" : "لا توجد بنوك"}
                emptyLabelEn={isArchive ? "No archived banks" : "No banks"}
                loadErrorAr="حدث خطأ أثناء تحميل البنوك"
                loadErrorEn="Failed to load banks"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default BanksTable;
